import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Hero.css';
import kisanVideo from '../assets/kisanvideo.mp4';
import kisanMan from '../assets/kisanman.mp4';
import sectionBg from '../assets/hero-bg.png';

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [dbHeroSlides, setDbHeroSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
        const envApiUrl = process.env.REACT_APP_API_URL?.trim();
        const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';
        const res = await fetch(`${API_URL}/home-cms`, {
          headers: {
          }
        });
        const json = await res.json();
        if (json.success && json.data && Array.isArray(json.data.heroSlides)) {
          setDbHeroSlides(json.data.heroSlides);
        }
      } catch (err) {
        console.error('Failed to fetch hero slides:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHeroSlides();
  }, []);

  const slides = useMemo(() => {
    const customSlides = dbHeroSlides.map((s, idx) => ({
      id: `db-${idx}`,
      title: s.heading || (
        <>
          {t('hero.slide1_title1', 'Products That')}<br />
          <span className="bold-text">{t('hero.slide1_title2', 'Derives Results')}</span>
        </>
      ),
      subtitle: s.subtitle || t('hero.slide1_sub', "Excellence in Every Product"),
      image: s.image,
      link: s.link
    }));

    const baseSlides = customSlides;

    return [
      ...baseSlides,
      {
        id: 2,
        video: kisanVideo
      },
      {
        id: 3,
        video: kisanMan
      }
    ];
  }, [t, dbHeroSlides]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  // Sync state with window custom control events from StatsBar
  useEffect(() => {
    const handlePlayEvent = () => {
      if (videoRef.current) {
        videoRef.current.play().catch(e => console.log(e));
        setIsMuted(videoRef.current.muted);
      }
    };
    const handlePauseEvent = () => {
      if (videoRef.current) videoRef.current.pause();
    };
    const handleSeekEvent = (e) => {
      if (videoRef.current) videoRef.current.currentTime = e.detail.time;
    };
    const handleMuteEvent = (e) => {
      if (videoRef.current) {
        videoRef.current.muted = e.detail.muted;
        setIsMuted(e.detail.muted);
      }
    };
    const handleSpeedEvent = (e) => {
      if (videoRef.current) videoRef.current.playbackRate = e.detail.rate;
    };
    const handlePipEvent = async () => {
      try {
        if (videoRef.current) {
          if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
          } else {
            await videoRef.current.requestPictureInPicture();
          }
        }
      } catch (error) {
        console.error("PiP error", error);
      }
    };
    const handleDownloadEvent = () => {
      const link = document.createElement('a');
      link.href = kisanMan;
      link.download = 'kisanman.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    window.addEventListener('play-brand-video', handlePlayEvent);
    window.addEventListener('pause-brand-video', handlePauseEvent);
    window.addEventListener('seek-brand-video', handleSeekEvent);
    window.addEventListener('mute-brand-video', handleMuteEvent);
    window.addEventListener('speed-brand-video', handleSpeedEvent);
    window.addEventListener('speed-brand-video-pip', handlePipEvent);
    window.addEventListener('speed-brand-video-download', handleDownloadEvent);

    return () => {
      window.removeEventListener('play-brand-video', handlePlayEvent);
      window.removeEventListener('pause-brand-video', handlePauseEvent);
      window.removeEventListener('seek-brand-video', handleSeekEvent);
      window.removeEventListener('mute-brand-video', handleMuteEvent);
      window.removeEventListener('speed-brand-video', handleSpeedEvent);
      window.removeEventListener('speed-brand-video-pip', handlePipEvent);
      window.removeEventListener('speed-brand-video-download', handleDownloadEvent);
    };
  }, []);

  // Timeupdate dispatcher
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      window.dispatchEvent(new CustomEvent('brand-video-timeupdate', { 
        detail: { currentTime: videoRef.current.currentTime } 
      }));
    }
  };

  // Metadata dispatcher
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      window.dispatchEvent(new CustomEvent('brand-video-metadata', { 
        detail: { duration: videoRef.current.duration } 
      }));
    }
  };

  // Status/Playback dispatcher
  const handlePlayStatus = (status) => {
    setIsPlaying(status);
    window.dispatchEvent(new CustomEvent('brand-video-status', { 
      detail: { isPlaying: status } 
    }));
  };

  // Volume dispatcher
  const handleVolumeChange = () => {
    if (videoRef.current) {
      setIsMuted(videoRef.current.muted);
      window.dispatchEvent(new CustomEvent('brand-video-volume', { 
        detail: { isMuted: videoRef.current.muted } 
      }));
    }
  };

  // Auto-slide transition for all image slides after 7 seconds
  useEffect(() => {
    const activeSlide = slides[currentSlide];
    if (!activeSlide) return;

    const slideImg = activeSlide.image || '';
    const isVideo = activeSlide.video || slideImg.endsWith('.mp4') || slideImg.endsWith('.webm') || slideImg.endsWith('.ogg');

    if (!isVideo) {
      const timer = setTimeout(() => {
        nextSlide();
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, nextSlide, slides]);

  // Pause brand song and reset controls on slide transition
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.playbackRate = 1;
    }
    handlePlayStatus(false);
  }, [currentSlide]);

  const resolveImageUrl = (imgName) => {
    if (!imgName) return sectionBg;
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      return imgName;
    }

    if (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) {
      const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.port === '3000';
      const envApiUrl = process.env.REACT_APP_API_URL?.trim();
      const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';
      let backendBase = isLocalHost ? 'http://localhost:5000' : API_URL.replace('/api', '');
      if (!backendBase || backendBase.startsWith('/')) {
        backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
      }
      return `${backendBase}${imgName.startsWith('/') ? '' : '/'}${imgName}`;
    }
    return sectionBg;
  };

  const [resolvedBgImage, setResolvedBgImage] = useState(sectionBg);

  useEffect(() => {
    let active = true;
    const activeSlide = slides[currentSlide];
    if (!activeSlide) return;

    const img = activeSlide.image || '';
    if (!img || activeSlide.video) {
      setResolvedBgImage(sectionBg);
      return;
    }

    const fallbackImages = [
      'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1592982537447-7440770cbfc9?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1595974482597-4b8da8879bc5?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1534013414124-0e02ba07f616?auto=format&fit=crop&w=1920&q=80'
    ];

    const url = resolveImageUrl(img);
    const tempImg = new Image();
    tempImg.src = url;
    tempImg.onload = () => {
      if (active) setResolvedBgImage(url);
    };
    tempImg.onerror = () => {
      if (active) setResolvedBgImage(fallbackImages[currentSlide % fallbackImages.length]);
    };

    return () => {
      active = false;
    };
  }, [currentSlide, slides]);

  const slideImg = slides[currentSlide].image || '';
  const isVideoSlide = slideImg.endsWith('.mp4') || slideImg.endsWith('.webm') || slideImg.endsWith('.ogg');
  const bgImage = slides[currentSlide].video === kisanMan 
    ? sectionBg 
    : resolveImageUrl(slideImg);

  if (isLoading) {
    return (
      <section className="hero" style={{ background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ borderTopColor: '#4ba242', width: '40px', height: '40px', borderWidth: '4px' }}></div>
      </section>
    );
  }

  return (
    <section className={`hero ${slides[currentSlide].video === kisanMan ? 'hero-music-active' : ''}`} style={(!isVideoSlide && resolvedBgImage) ? { backgroundImage: `url(${resolvedBgImage})` } : {}}>
      {/* Custom Full-Screen Video Background for Uploaded Video Slides */}
      {isVideoSlide && (
        <video
          key={bgImage}
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
          style={{ objectFit: 'cover', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1 }}
        >
          <source src={bgImage} type={`video/${bgImage.split('.').pop()}`} />
        </video>
      )}

      {/* Slide 2: Normal Full-Screen Video Background */}
      {slides[currentSlide].video === kisanVideo && (
        <video
          key={kisanVideo}
          className="hero-video"
          autoPlay
          onEnded={nextSlide}
          muted={true}
          playsInline
          style={{ cursor: 'pointer' }}
        >
          <source src={kisanVideo} type="video/mp4" />
        </video>
      )}

      {/* Slide 3: Centered Video Card Music Player */}
      {slides[currentSlide].video === kisanMan ? (
        <div className="hero-music-player-container">
          <div className="music-video-header">
            <div className="music-note-header">
              <span className="note-icon">🎵</span>
            </div>
            <h1 className="music-video-title">
              {t('hero.listen_brand', 'Listen to the Sound of Our Brand')}
            </h1>
          </div>

          <div className="music-player-wrapper">
            <div className="video-card" onClick={() => {
              if (videoRef.current) {
                if (videoRef.current.paused) {
                  videoRef.current.play().catch(err => console.error("Video play error:", err));
                  videoRef.current.muted = false;
                  setIsMuted(false);
                } else {
                  videoRef.current.pause();
                }
              }
            }} style={{ cursor: 'pointer' }}>
              <div className={`video-card-led ${isPlaying ? 'playing' : 'paused'}`}></div>
              <video
                ref={videoRef}
                key={kisanMan}
                src={kisanMan}
                onPlay={() => handlePlayStatus(true)}
                onPause={() => handlePlayStatus(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onVolumeChange={handleVolumeChange}
                className="player-video"
                muted={isMuted}
                playsInline
              />
              {!isPlaying && (
                <div className="center-play-overlay">
                  <div className="play-triangle"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="hero-overlay"></div>
          <div className="container">
            {slides[currentSlide].title && (
              <div className="hero-content">
                <h1 className="hero-title">
                  {slides[currentSlide].title}
                </h1>
                <p className="hero-subtitle">{slides[currentSlide].subtitle}</p>
                <button className="btn-hero" onClick={() => navigate(slides[currentSlide].link || '/products')}>{t('hero.explore')}</button>
              </div>
            )}
          </div>
        </>
      )}
      
      <div className="slider-controls">
        <button className="slider-arrow prev" onClick={prevSlide}>❮</button>
        <button className="slider-arrow next" onClick={nextSlide}>❯</button>
      </div>
      
      <div className="slider-dots">
        {slides.map((_, index) => (
          <div 
            key={index} 
            className={`dot ${currentSlide === index ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
          ></div>
        ))}
      </div>
    </section>
  );
};

export default Hero;


